import type OwnHousing from '~/models/OwnHousing';
import type Housing from '~/models/Housing';
import { defineStore } from 'pinia';

export interface HousingState extends OwnHousing {
  housings: Record</*housingId*/ number, Housing>;
  userHousings: Record</*userGuid*/ string, Housing>;
}

// TODO: Change to setup store: https://pinia.vuejs.org/core-concepts/#Setup-Stores
export const useHousingStore = defineStore('housing', {
  state: (): HousingState => ({
    housings: {},
    userHousings: {},

    id: 0,
    userId: 0,
    isActive: false,
    exportInfo: {
      activeExports: [],
      isGenerallyExportable: false,
      meetsQualityCriteria: false,
    },
    geoId: 0,
    geo: {
      id: 0,
      country: '',
      region: '',
      city: '',
      area: '',
      zip: '',
      iso2Country: '',
      iso3Country: '',
      isoCurrency: '',
    },
    street: '',
    houseNumber: '',
    title: '',
    description: '',
    rooms: 0,
    rent: 0,
    sideCosts: 0,
    size: 0,
    type: {
      id: 0,
      display: '',
    },
    storey: 0,
    deposit: 0,
    moveInDate: null,
    moveOutDate: null,
    housingProperties: [],
    images: [],
    hasMoreThanOneImage: false,
    previewImage: {
      id: 0,
      title: '',
      filename: '',
      urlPaths: {
        original: '',
        large: '',
        medium: '',
        small: '',
        thumb: '',
      },
    },
    uriString: '',
    shortURL: '',
    isPremium: false,
    isMatch: false,
    isHidden: false,
  }),

  getters: {
    housing: (state) => ({ ...state } as OwnHousing),
  },

  actions: {
    overwrite(o: OwnHousing) {
      this.id = o.id;
      this.userId = o.userId;
      this.isActive = o.isActive;
      this.exportInfo = o.exportInfo;
      this.geoId = o.geoId;

      this.geo.id = o.geo.id;
      this.geo.country = o.geo.country;
      this.geo.region = o.geo.region;
      this.geo.city = o.geo.city;
      this.geo.area = o.geo.area;
      this.geo.zip = o.geo.zip;

      this.street = o.street;
      this.houseNumber = o.houseNumber;
      this.title = o.title;
      this.description = o.description;
      this.rooms = o.rooms;
      this.rent = o.rent;
      this.sideCosts = o.sideCosts;
      this.size = o.size;
      this.type = o.type;
      this.storey = o.storey;
      this.deposit = o.deposit;
      this.moveInDate = o.moveInDate;
      this.moveOutDate = o.moveOutDate;
      this.housingProperties = o.housingProperties;

      this.images = o.images;
      this.hasMoreThanOneImage = o.hasMoreThanOneImage;

      this.previewImage.id = o.previewImage.id;
      this.previewImage.title = o.previewImage.title;
      this.previewImage.filename = o.previewImage.filename;
      this.previewImage.urlPaths.original = o.previewImage.urlPaths.original;
      this.previewImage.urlPaths.large = o.previewImage.urlPaths.large;
      this.previewImage.urlPaths.medium = o.previewImage.urlPaths.medium;
      this.previewImage.urlPaths.small = o.previewImage.urlPaths.small;
      this.previewImage.urlPaths.thumb = o.previewImage.urlPaths.thumb;

      this.uriString = o.uriString;
      this.shortURL = o.shortURL;
      this.isPremium = o.isPremium;
      this.isMatch = o.isMatch;
      this.isHidden = o.isHidden;

      this.addHousing({
        id: o.id,
        userId: o.userId,
        isActive: o.isActive,
        geoId: o.geoId,
        geo: o.geo,
        street: o.street,
        houseNumber: o.houseNumber,
        title: o.title,
        description: o.description,
        rooms: o.rooms,
        rent: o.rent,
        sideCosts: o.sideCosts,
        size: o.size,
        type: o.type,
        storey: o.storey,
        deposit: o.deposit,
        moveInDate: o.moveInDate,
        moveOutDate: o.moveOutDate,
        housingProperties: o.housingProperties,
        images: o.images,
        hasMoreThanOneImage: o.hasMoreThanOneImage,
        previewImage: o.previewImage,
        uriString: o.uriString,
        shortURL: o.shortURL,
        isPremium: o.isPremium,
        isMatch: o.isMatch,
        isHidden: o.isHidden,
      });
    },

    addHousing(housing: Housing) {
      this.housings[housing.id] = housing;
      this.userHousings[housing.userId] = housing;
    },
    getHousing(id: number): Housing | null {
      return this.housings[id] || null;
    },
    getHousingForUser(guid: string): Housing | null {
      return this.userHousings[guid] || null;
    },

    async activateHousing(): Promise<void> {
      const { housingAPI } = useBackendRequests();
      try {
        await housingAPI.activate();
        this.isActive = true;
      } catch (error) {
        throw error;
      }
    },

    async deactivateHousing(reasonRadio = 'Tauschwohnung.com gefällt mir nicht', reasonText = ''): Promise<void> {
      const { housingAPI } = useBackendRequests();
      try {
        await housingAPI.deactivate(reasonRadio, reasonText);
        this.isActive = false;
      } catch (error) {
        throw error;
      }
    },
  },
});

export default useHousingStore;
